(ns hai.web-app.universal-bar
  "Universal \"Add anything…\" bar for the desktop top chrome.

  A centered pill in the top bar that opens a command palette (also
  reachable from anywhere with ⌘K / Ctrl+K or `/`) for quickly adding a
  note or a task. Mobile uses its own bottom-sheet flow that drives the
  legacy modals in hai.web-app.add-menu."
  (:require
   [clojure.string :as str]
   [field :as-alias field]
   [garden.stylesheet]
   [hai.contacts.helpers :as contacts]
   [hai.admin-ui.data-tasks.helpers :as tasks]
   [hai.tasks.data :as tasks-data]
   [hai.ui.components.form.core :as form]
   [hai-model.model :as model]
   [hai.library.prompts.helper :as prompts]
   [hai.knowledge.assistants.helper :as assistants]
   [hai.ui.components.form.input :as input]
   [hai.ui.components.spinners :as spinners]
   ;; :as-alias — a loading require would close a cycle (layout → universal-bar
   ;; → create → … sharing.components → layout); only the event keyword is used.
   [hai.notes.create :as-alias note-create]
   [hai.notes.helpers :as notes]
   [hai.mini-apps.common :as mini-apps]
   [hai.ui.components.form.select.components :as select.components]
   [hai.ui.components.form.select.multi-select :as multi-select]
   [hai.ui.components.form.select.searchable-multi-select :as searchable-select]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e! defevent defevent-fx defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.add-menu :as add-menu]
   [hai.web-app.platform :as platform]
   [hai.web-app.util.rf-storage :as rf-storage]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private shortcut-label (if platform/mac? "⌘K" "Ctrl+K"))

;; ---------------------------------------------------------------------------
;; State
;; ---------------------------------------------------------------------------

;; Entity types that can be created from the omnibar in the order they will be shown in the modal
(def creatable-entity-types [:note :task])

(defsub ::open? [db] (boolean (::open? db)))

(defsub ::pulsing? [db] (boolean (::pulsing? db)))

(defn db->selected-value
  [db]
  (or (::selected-value db)
      (get-in db [:settings/local ::last-kind])))

(defsub ::selected-value
  [db]
  (db->selected-value db))

(defevent ::open [db _]
  (let [entity-type (or (db->selected-value db)
                        (first creatable-entity-types))]
    (-> db
        (assoc ::open? true)
        (assoc ::selected-value entity-type))))

(defevent-fx ::close
  [{:keys [db]}]
  {:db (assoc db ::open? false)
   :dispatch [:form.field/clear! [::palette ::search] :table/search]})

(defn db->search-input-value
  [db]
  (some-> (form/->field-current-value db [::palette ::search] :table/search)
          ;; TODO I'm pretty sure the form input field does this already?
          (str/trim)
          (not-empty)))

(defsub ::search-input-value
  [db]
  (db->search-input-value db))

;; TODO merge with toggle-option
(defevent ::set-kind
  <-> [(rf-storage/settings-local)]
  [db kind]
  (-> db
      (assoc ::selected-value kind)
      (assoc-in [:settings/local ::last-kind] kind)))

(defevent-fx ::adjacent-option
  <-> [(rf-storage/settings-local)]
  [{:keys [db]} direction]
  (let [{search-results :table/rows} (form/db->current-value db [::palette ::search])
        options (into creatable-entity-types search-results)
        current (db->selected-value db)
        idx (max 0 (.indexOf options current))
        step (if (= direction :prev) -1 1)
        next-option (nth options (mod (+ idx step) (count options)))]
    {:dispatch [::toggle-option next-option]}))

(defevent ::toggle-option
  <-> [(rf-storage/settings-local)]
  [db option]
  (cond-> (assoc db ::selected-value option)
    (keyword? option) (assoc-in [:settings/local ::last-kind] option)))

(defevent-fx ::select-option
  [{:keys [db]}]
  (let [{search-input :table/search} (form/db->current-value db [::palette ::search])
        selected-option (db->selected-value db)]
    (cond
      (map? selected-option)
      (let [route (case (model/->name selected-option)
                    :prompt [:ai-prompts/edit selected-option]
                    :task [:tasks/detail selected-option]
                    :note [:notes/detail selected-option]
                    nil)]
        (if route
          {:dispatch-n [[::close]
                        [:routing/navigate route]]}
          {:dispatch [:toast/error {:text "Coming soon!"}]}))

      (and (keyword? selected-option)
           (empty? search-input))
      {:dispatch-n [[::close]
                    [(case selected-option
                       :note ::note-create/open
                       :task ::add-menu/open-task-modal)]]}

      :else
      {:dispatch-n [[::set-kind selected-option]
                    [::create-entity]]})))

(defevent-fx ::create-entity
  [{:keys [db]} _]
  (let [trimmed (db->search-input-value db)
        kind (db->selected-value db)]
    {:db (-> db
             (assoc ::open? false)
             (assoc ::pulsing? true))
     :dispatch-n [(case kind
                    :note [::add-menu/add-note trimmed]
                    :task [::tasks-data/create {:title trimmed}])
                  [:form.field/clear! [::palette ::search] :table/search]]
     :dispatch-later {:ms 700 :dispatch [::clear-pulse]}}))

(defevent ::clear-pulse [db _]
  (assoc db ::pulsing? false))

;; ---------------------------------------------------------------------------
;; Global keyboard
;; ---------------------------------------------------------------------------

(defn- typing? []
  (let [el (.-activeElement js/document)]
    (when el
      (let [tag (some-> (.-tagName el) (.toUpperCase))]
        (or (= tag "INPUT")
            (= tag "TEXTAREA")
            (= tag "SELECT")
            (true? (.-isContentEditable el)))))))

(defn- handle-global-keydown [e]
  ;; App-wide OPEN shortcuts only.
  ;; Escape is deliberately NOT handled here -- it belongs to `open-palette`.
  (let [k (some-> (.-key e) (.toLowerCase))]
    (cond
      (and (= k "k") (or (.-metaKey e) (.-ctrlKey e)))
      (do (.preventDefault e) (>e [::open]))

      (and (= k "/") (not (typing?)))
      (do (.preventDefault e) (>e [::open])))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

(o/defstyled kbd :span
  {:font-family "'JetBrains Mono', ui-monospace, monospace"
   :font-size "11px"
   :font-weight "500"
   :padding "2px 6px"
   :border-radius "5px"
   :background "rgba(31,36,24,0.07)"
   :color t/--property-4
   :border "1px solid rgba(0,0,0,0.04)"
   :line-height "1"
   :white-space "nowrap"
   :display "inline-flex"
   :align-items "center"
   :gap "2px"}
  [:&.lavender
   {:background "rgba(110,95,219,0.14)"
    :color t/--property-5}])

(o/defstyled bar-pill :button
  {:display "flex"
   :align-items "center"
   :gap "10px"
   :width "min(540px, 100%)"
   :padding "10px 12px 10px 16px"
   :border-radius "12px"
   :background t/--property-6
   :border "1px solid rgba(31,36,24,0.14)"
   :cursor "text"
   :transition "border-color 120ms ease, box-shadow 120ms ease"
   :font-family "inherit"
   :font-size "14px"
   :font-weight "400"
   :color t/--property-4
   :height "40px"}
  [:&:hover {:border-color "rgba(110,95,219,0.45)"}]
  [:&:focus-visible
   {:outline "none"
    :border-color "rgba(110,95,219,0.55)"
    :box-shadow "0 0 0 3px rgba(110,95,219,0.18)"}]
  [:.placeholder {:flex "1" :text-align "left" :color t/--property-4}]
  [:&.pulsing
   {:animation "universal-bar-pulse 600ms ease-out"}])

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules pulse-keyframes
  (garden.stylesheet/at-keyframes
   :universal-bar-pulse
   ["0%"   {:box-shadow "0 0 0 0 rgba(110,95,219,0.45)"}]
   ["100%" {:box-shadow "0 0 0 14px rgba(110,95,219,0)"}]))

(o/defstyled center-slot :div
  {:position "absolute"
   :left "50%"
   :top "50%"
   :transform "translate(-50%, -50%)"
   :width "min(540px, calc(100% - 240px))"
   :display "flex"
   :align-items "center"
   :justify-content "center"
   :pointer-events "none"}
  [bar-pill {:pointer-events "auto"}])

(o/defstyled palette-backdrop :div
  {:position "fixed"
   :inset "0"
   :background "rgba(20,18,14,0.32)"
   :backdrop-filter "blur(2px)"
   :z-index "500"
   :animation "universal-bar-scrim-in 140ms ease-out"})

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules scrim-keyframes
  (garden.stylesheet/at-keyframes
   :universal-bar-scrim-in
   ["0%"   {:opacity "0"}]
   ["100%" {:opacity "1"}]))

(o/defstyled palette-card :div
  {:position "fixed"
   :top "18vh"
   :left "50%"
   :transform "translateX(-50%)"
   :width "min(640px, calc(100vw - 48px))"
   :background t/--colour-white
   :border-radius "16px"
   :box-shadow "0 30px 80px -20px rgba(20,18,14,0.45), 0 0 0 1px rgba(0,0,0,0.05)"
   :z-index "600"
   :animation "universal-bar-palette-in 180ms cubic-bezier(.2,.9,.3,1)"
   :display "flex"
   :flex-direction "column"
   :overflow "hidden"})

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules palette-keyframes
  (garden.stylesheet/at-keyframes
   :universal-bar-palette-in
   ["0%"   {:opacity "0" :transform "translate(-50%, -8px) scale(0.98)"}]
   ["100%" {:opacity "1" :transform "translate(-50%, 0) scale(1)"}]))

(o/defstyled palette-input-row :div
  {:display "flex"
   :align-items "center"
   :gap "12px"
   :padding "16px 18px"
   :border-bottom "1px solid rgba(31,36,24,0.08)"})

(o/defstyled palette-rows :div
  {:padding "6px"
   :display "flex"
   :flex-direction "column"
   :gap "2px"})

(o/defstyled palette-option :div
  {:display "flex"
   :align-items "center"
   :gap "14px"
   :padding "12px 14px"
   :border-radius "10px"
   :background "transparent"
   :cursor "pointer"
   :transition "background-color 120ms ease"}
  [:&.highlighted
   {:background "rgba(110,95,219,0.10)"}
   [:.label {:font-weight "600"}]]
  [:.icon-tile
   {:width "32px" :height "32px"
    :border-radius "8px"
    :display "flex"
    :align-items "center"
    :justify-content "center"
    :flex-shrink "0"
    :color t/--property-7}
   ;; Normalize glyph size across entity types — mui-icons (e.g. the tasks
   ;; :task_alt) default to 16px and ignore the :size prop.
   [:svg {:width "18px" :height "18px"}]]
  [:.body
   {:flex "1"
    :min-width "0"
    :display "flex"
    :flex-direction "column"
    :gap "2px"}]
  [:.label
   {:font-size "14px"
    :color t/--property-7
    :line-height "1.3"
    :word-break "break-word"}])

(o/defstyled palette-section-label :div
  {:padding "8px 14px 4px"
   :font-size "11px"
   :font-weight "600"
   :letter-spacing "0.04em"
   :text-transform "uppercase"
   :color "#6e7560"})

(o/defstyled palette-footer :div
  {:display "flex"
   :align-items "center"
   :gap "12px"
   :padding "10px 18px"
   :background t/--property-6
   :border-top "1px solid rgba(31,36,24,0.08)"
   :font-size "12px"
   :color t/--property-8}
  [:.hints {:flex "1"
            :display "flex"
            :align-items "center"
            :gap "12px"}]
  [:.hint {:display "inline-flex" :align-items "center" :gap "6px"}]
  [:.right {:margin-left "auto"}])

;; ---------------------------------------------------------------------------
;; Views
;; ---------------------------------------------------------------------------

(defn- bar-trigger []
  (r/with-let [_ (.addEventListener js/window "keydown" handle-global-keydown)]
    (let [pulsing? @(<s [::pulsing?])]
      [bar-pill
       {:type "button"
        :class (when pulsing? "pulsing")
        :aria-label "Open Add anything bar"
        :on-click #(>e [::open])}
       [icons/outlined-search {:size 18}]
       [:span.placeholder "Search or add new …"]
       [kbd shortcut-label]])
    (finally
      (.removeEventListener js/window "keydown" handle-global-keydown))))

(defn trigger
  "Bar trigger placed inside an absolutely-positioned center slot. The
   parent (top-bar) is sticky which establishes the positioning context."
  []
  [center-slot [bar-trigger]])

(defn- add-entity-row
  [{:keys [kind highlighted? text label]}]
  (let [note? (= kind :note)
        tint (if note? t/--property-9 t/--property-10)
        search-field-value @(<s [::search-input-value])
        label (or label
                  (str (if note? "Add new note" "Add new task")
                       (when search-field-value
                         (str " · \"" text "\""))))]
    [palette-option
     {:class (when highlighted? "highlighted")
      :role "option"
      :aria-selected highlighted?
      :on-mouse-enter #(>e! [::toggle-option kind])
      :on-click #(>e! [::select-option])}
     [:div.icon-tile {:style {:background (str "var(" tint ")")}}
      (if note? [notes/icon {:size 18}] [tasks/icon {:size 18}])]
     [:div.body
      [:div.label label]]
     [kbd {:class (when highlighted? "lavender")} "↵"]]))

(defn- search-result-row
  [{:keys [entity highlighted?]}]
  (let [{:keys [label icon tint]}
        (case (model/->name entity)
          ;; TODO this should be in a general helpers file
          :assistant {:label (assistants/->label entity)
                      :icon (assistants/->icon entity)
                      :tint (assistants/->tint entity)}
          :prompt {:label (prompts/->label entity)
                   :icon (prompts/->icon entity)
                   :tint (prompts/->tint entity)}
          :mini-app {:label (mini-apps/->label entity)
                     :icon (mini-apps/->icon entity)
                     :tint (mini-apps/->tint entity)}
          :contact {:label (contacts/->label entity)
                    :icon (contacts/->icon entity)
                    :tint (contacts/->tint entity)}
          :task {:label (tasks/->label entity)
                 :icon (tasks/->icon entity)
                 :tint (tasks/->tint entity)}
          :note {:label (notes/->label entity)
                 :icon (notes/->icon entity)
                 :tint (notes/->tint entity)}
          nil)]

    [palette-option
     {:class (when highlighted? "highlighted")
      :role "option"
      :aria-selected highlighted?
      :on-mouse-enter #(>e! [::toggle-option entity])
      :on-click #(>e! [::select-option])}
     (when icon
       [:div.icon-tile {:style {:background (str "var(" tint "")}}
        [icon {:size 18}]])
     [:div.body
      [:div.label (subs (str label) 0 150)]]
     [kbd {:class (when highlighted? "lavender")} "↵"]]))

(o/defstyled search-input* :div
  {:flex "1"
   :border "none"
   :background "transparent"
   :outline "none"
   :font-size "17px"
   :font-family "inherit"
   :color "#1f2418"}
  ([select]
   [:<> [multi-select/select-input**
         (assoc select
                :input/render input/no-styling
                :form.field.input/auto-focus? true)]]))

(o/defstyled searching-indicator :div
  [:.dots-row {:display "flex"
               :justify-content "center"
               :padding "4px 0 8px"}]
  ([]
   [:<>
    [palette-section-label "Search in progress"]
    [:div.dots-row
     [spinners/dots]]]))

(defn search-input
  [field]
  [searchable-select/field
   (assoc field
          :query [{:user/search [{:collection/items
                                  {:prompt [:ai-prompt/uuid
                                            :ai-prompt/title
                                            :ai-prompt/content]
                                   :assistant [:ai-model-config/uuid
                                               :ai-model-config/title]
                                   :mini-app [:mini-app/uuid
                                              :mini-app/title
                                              :mini-app/description
                                              :mini-app/tool]
                                   :contact [:contact/uuid
                                             :contact/full-name]
                                   :note [:note/uuid
                                          :note/text]
                                   :task [:task/uuid
                                          :task/title]}}]}]
          :render {:style select.components/radix
                   :field search-input*}
          :auto-focus true
          :placeholder "Search or add new …")])

(defn- close-on-escape
  "Closes the palette on Escape. Registered on `document` only while the palette
   is open (see `open-palette`), so it needs no open? check and fires wherever
   focus is inside the dialog -- not only when the search input has focus. A
   stable top-level fn so add/removeEventListener see the same reference."
  [e]
  (when (= "Escape" (.-key e))
    (.preventDefault e)
    (>e! [::close])))

(defn- keep-input-focus
  "The palette uses a virtual-focus pattern: real focus stays in the search
   input, and Arrow/Tab move a highlight via the input's key handler. Clicking
   non-interactive space in the dialog would blur the input (focus falls to
   <body>), so Tab would revert to native tabbing and walk out to the top bar.
   Preventing mousedown's default for non-focusable targets keeps focus on the
   input; genuinely focusable controls are exempt so they still focus/click
   normally (mousedown's default is only focus, so click/select still fire)."
  [e]
  (when-not (some-> (.-target e)
                    (.closest "input, textarea, select, button, a, [tabindex], [contenteditable=true]"))
    (.preventDefault e)))

(defn- open-palette
  "The palette overlay, rendered only while open. Owns a document-level Escape
   listener for exactly its mounted lifetime -- replacing the old always-on
   global Escape handling, and covering the case where focus has left the search
   input (e.g. after clicking empty space in the dialog)."
  [fields]
  (r/with-let [_ (.addEventListener js/document "keydown" close-on-escape)]
    (let [search-field-value @(<s [:form/current-value [::palette ::search]])
          text (:table/search search-field-value)
          search-results (:table/rows search-field-value)
          trimmed (str/trim (or text ""))
          selected-value @(<s [::selected-value])
          on-key (fn [e]
                   ;; Escape is handled by `close-on-escape` at the document level
                   (case (.-key e)
                     ("ArrowDown" "Tab")
                     (do (.preventDefault e)
                         (>e! [::adjacent-option :next]))

                     "ArrowUp"
                     (do (.preventDefault e)
                         (>e! [::adjacent-option :prev]))

                     "Enter"
                     (do (.preventDefault e)
                         (>e! [::select-option]))

                     nil))]
      [:<>
       [palette-backdrop {:on-click #(>e [::close])}]
       [palette-card
        {:role "dialog"
         :aria-modal "true"
         :aria-label "Add anything"
         :on-click #(.stopPropagation %)
         :on-mouse-down keep-input-focus}
        [palette-input-row
         [:span.plus [icons/outlined-search {:size 18}]]
         [search-input (assoc (::search fields)
                              :on-key-down on-key)]
         [kbd "esc"]]
        (into [palette-rows
               [add-entity-row
                {:kind :note
                 :highlighted? (= selected-value :note)
                 :text trimmed}]
               [add-entity-row
                {:kind :task
                 :highlighted? (= selected-value :task)
                 :text trimmed}]
               (cond
                 @(<s [:table/resolving? [::palette ::search]])
                 [searching-indicator]

                 (seq search-results)
                 [palette-section-label "Search Results"]

                 (and (seq trimmed)
                      (empty? search-results))
                 [:div [palette-section-label "Search results"]
                  [palette-option {:style {:justify-content "center"}}
                   [:div.label "No search results found"]]])]
              (map (fn [entity]
                     [search-result-row
                      {:entity entity
                       :highlighted? (= selected-value entity)}]))
              search-results)
        [palette-footer
         [:span.hints
          [:span.hint [kbd "↵"] "add"]
          [:span.hint [kbd "tab"] "switch"]
          [:span.hint [kbd "esc"] "close"]]]]])
    (finally
      (.removeEventListener js/document "keydown" close-on-escape))))

(o/defstyled render :div
  :flex :flex-col :gap-3
  :w-100
  [:.buttons :flex :flex-row :gap-3]
  ([{:form/keys [fields]}]
   (when @(<s [::open?])
     [open-palette fields])))

(defn palette
  "Renders the command palette overlay when open. Mount once in the desktop
   chrome alongside the main content."
  []
  [form/component
   {:id ::palette
    :render render
    :schema [:map [::search :string]]}])
